import React, { useState, useEffect } from 'react'
import { 
  Bell, Trash2, Check, X, Eye, Send,
  AlertCircle, Info, CheckCircle, XCircle,
  Users, User, Filter, Search
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

interface Notification {
  id: string
  user_id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  read_at?: string
  created_at: string
  action_url?: string
  metadata?: any
}

interface User {
  id: string
  username: string
  display_name: string
  is_parent: boolean
  color_theme: string
}

const NOTIFICATION_TYPES = [
  { value: 'info', label: 'Info', icon: Info, color: 'bg-blue-500' },
  { value: 'success', label: 'Success', icon: CheckCircle, color: 'bg-green-500' },
  { value: 'warning', label: 'Warning', icon: AlertCircle, color: 'bg-yellow-500' },
  { value: 'error', label: 'Error', icon: XCircle, color: 'bg-red-500' }
]

export const NotificationManager: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showSendNotification, setShowSendNotification] = useState(false)
  
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'info' as const,
    recipients: [] as string[],
    sendToAll: false,
    sendToParents: false,
    sendToKids: false
  })

  useEffect(() => {
    loadData()
  }, [filter, selectedUser])

  const loadData = async () => {
    setLoading(true)
    try {
      const [notificationsData, usersData] = await Promise.all([
        apiClient.get(`/api/notifications${selectedUser ? `?user_id=${selectedUser}` : ''}${filter !== 'all' ? `&filter=${filter}` : ''}`),
        apiClient.get('/api/users')
      ])
      setNotifications(notificationsData)
      setUsers(usersData)
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await apiClient.put(`/api/notifications/${notificationId}/read`)
      toast.success('Notification marked as read')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to mark as read')
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await apiClient.put('/api/notifications/mark-all-read')
      toast.success('All notifications marked as read')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to mark all as read')
    }
  }

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await apiClient.delete(`/api/notifications/${notificationId}`)
      toast.success('Notification deleted')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete notification')
    }
  }

  const handleDeleteAll = async () => {
    if (!confirm('Are you sure you want to delete all notifications?')) return
    
    try {
      await apiClient.delete('/api/notifications/delete-all')
      toast.success('All notifications deleted')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete all notifications')
    }
  }

  const handleSendNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      toast.error('Please provide title and message')
      return
    }

    let recipients = [...newNotification.recipients]
    
    if (newNotification.sendToAll) {
      recipients = users.map(u => u.id)
    } else {
      if (newNotification.sendToParents) {
        recipients.push(...users.filter(u => u.is_parent).map(u => u.id))
      }
      if (newNotification.sendToKids) {
        recipients.push(...users.filter(u => !u.is_parent).map(u => u.id))
      }
    }

    if (recipients.length === 0) {
      toast.error('Please select at least one recipient')
      return
    }

    try {
      await apiClient.post('/api/notifications/send', {
        title: newNotification.title,
        message: newNotification.message,
        type: newNotification.type,
        user_ids: [...new Set(recipients)]
      })
      toast.success('Notification sent successfully')
      setShowSendNotification(false)
      setNewNotification({
        title: '',
        message: '',
        type: 'info',
        recipients: [],
        sendToAll: false,
        sendToParents: false,
        sendToKids: false
      })
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send notification')
    }
  }

  const getTypeInfo = (type: string) => {
    return NOTIFICATION_TYPES.find(t => t.value === type) || NOTIFICATION_TYPES[0]
  }

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId)
    return user ? user.display_name : 'Unknown'
  }

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          notification.message.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Notification Management</h2>
        <p className="text-gray-600">View and manage system notifications</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold">{notifications.length}</p>
            </div>
            <Bell className="w-8 h-8 text-gray-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Unread</p>
              <p className="text-2xl font-bold text-orange-600">{unreadCount}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Read</p>
              <p className="text-2xl font-bold text-green-600">
                {notifications.length - unreadCount}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Users</p>
              <p className="text-2xl font-bold">{users.length}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Notifications</option>
              <option value="unread">Unread Only</option>
              <option value="read">Read Only</option>
            </select>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Users</option>
              {Array.isArray(users) && users.map(user => (
                <option key={user.id} value={user.id}>{user.display_name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Mark All Read
            </button>
            <button
              onClick={handleDeleteAll}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              Delete All
            </button>
            <button
              onClick={() => setShowSendNotification(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Notification
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {filteredNotifications.map(notification => {
            const typeInfo = getTypeInfo(notification.type)
            const user = users.find(u => u.id === notification.user_id)
            
            return (
              <div 
                key={notification.id} 
                className={`p-4 ${!notification.read ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className={`p-2 rounded-lg ${typeInfo.color} bg-opacity-10 mr-3`}>
                      <typeInfo.icon className={`w-5 h-5 ${typeInfo.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <h4 className="font-semibold text-gray-900">{notification.title}</h4>
                        {!notification.read && (
                          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{notification.message}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        {user && (
                          <div className="flex items-center mr-4">
                            <User className="w-3 h-3 mr-1" />
                            {user.display_name}
                          </div>
                        )}
                        <span>{format(parseISO(notification.created_at), 'MMM d, h:mm a')}</span>
                        {notification.read_at && (
                          <span className="ml-4">
                            Read: {format(parseISO(notification.read_at), 'MMM d, h:mm a')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {!notification.read && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="p-1 text-green-600 hover:text-green-900"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteNotification(notification.id)}
                      className="p-1 text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        
        {filteredNotifications.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No notifications found
          </div>
        )}
      </div>

      {/* Send Notification Modal */}
      {showSendNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Send Notification</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  value={newNotification.title}
                  onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Notification title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Message *</label>
                <textarea
                  value={newNotification.message}
                  onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Notification message..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={newNotification.type}
                  onChange={(e) => setNewNotification({ ...newNotification, type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {NOTIFICATION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Recipients</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newNotification.sendToAll}
                      onChange={(e) => setNewNotification({ 
                        ...newNotification, 
                        sendToAll: e.target.checked,
                        sendToParents: false,
                        sendToKids: false
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">Send to all users</span>
                  </label>
                  
                  {!newNotification.sendToAll && (
                    <>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newNotification.sendToParents}
                          onChange={(e) => setNewNotification({ 
                            ...newNotification, 
                            sendToParents: e.target.checked 
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm">Send to all parents</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newNotification.sendToKids}
                          onChange={(e) => setNewNotification({ 
                            ...newNotification, 
                            sendToKids: e.target.checked 
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm">Send to all kids</span>
                      </label>

                      <div className="border rounded-lg p-2 max-h-32 overflow-y-auto">
                        {Array.isArray(users) && users.map(user => (
                          <label key={user.id} className="flex items-center mb-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newNotification.recipients.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewNotification({
                                    ...newNotification,
                                    recipients: [...newNotification.recipients, user.id]
                                  })
                                } else {
                                  setNewNotification({
                                    ...newNotification,
                                    recipients: newNotification.recipients.filter(id => id !== user.id)
                                  })
                                }
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">
                              {user.display_name} {user.is_parent && '(Parent)'}
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowSendNotification(false)
                  setNewNotification({
                    title: '',
                    message: '',
                    type: 'info',
                    recipients: [],
                    sendToAll: false,
                    sendToParents: false,
                    sendToKids: false
                  })
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSendNotification}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <Send className="w-4 h-4 mr-2" />
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}